import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAccounts, useAccountFilterOptions, useExportAccounts, AccountFilters } from '@/hooks/useAccounts';
import { AccountsTable, AccountsFilterPanel } from '@/components/accounts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, Eye, EyeOff } from 'lucide-react';
import { convertToCSV, downloadFile, formatDateForFilename } from '@/lib/export-utils';
import { CreateAccountDialog } from '@/components/accounts/CreateAccountDialog';
import { toast } from 'sonner';
import { useUserScope } from '@/hooks/useUserScope';
import { REP_TERRITORY_MAP } from '@/config/filters';

// ── URL param helpers ──────────────────────────────────────────────
const ARRAY_KEYS: (keyof AccountFilters)[] = [
  'territories', 'countries', 'carrModalities', 'modalities', 'therapeuticAreas',
  'clinicalPhases', 'fundingStatuses', 'employeeRanges', 'accountTiers',
  'icpTiers', 'primaryCategories', 'subcategories',
];

function parseFiltersFromUrl(params: URLSearchParams): Partial<AccountFilters> {
  const out: Record<string, any> = {};
  const s = params.get('search');
  if (s) out.searchTerm = s;
  for (const k of ARRAY_KEYS) {
    const v = params.get(k as string);
    if (v) out[k as string] = v.split(',').filter(Boolean);
  }
  if (params.has('hideInactive')) out.hideInactive = params.get('hideInactive') === 'true';
  if (params.has('topAccount')) out.topAccount = params.get('topAccount') === 'true';
  if (params.has('page')) out.offset = (Number(params.get('page')) - 1) * 25;
  return out;
}

function syncFiltersToUrl(filters: AccountFilters, setParams: (p: URLSearchParams, opts?: any) => void) {
  const p = new URLSearchParams();
  if (filters.searchTerm) p.set('search', filters.searchTerm);
  for (const k of ARRAY_KEYS) {
    const v = filters[k] as string[] | undefined;
    if (v && v.length > 0) p.set(k as string, v.join(','));
  }
  if (filters.hideInactive === false) p.set('hideInactive', 'false');
  if (filters.topAccount) p.set('topAccount', 'true');
  const page = Math.floor((filters.offset || 0) / (filters.limit || 25)) + 1;
  if (page > 1) p.set('page', String(page));
  setParams(p, { replace: true });
}

// ── Component ─────────────────────────────────────────────────────
const Accounts = () => {
  const { shouldScope, salesRepName } = useUserScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scopeActive, setScopeActive] = useState(shouldScope);
  const initializedRef = useRef(false);

  // Initialize filters from URL or defaults
  const [filters, setFilters] = useState<AccountFilters>(() => {
    const fromUrl = parseFiltersFromUrl(searchParams);
    const hasUrlFilters = Object.keys(fromUrl).length > 0;
    return {
      limit: 25,
      offset: 0,
      hideInactive: true,
      ...(hasUrlFilters ? fromUrl : {}),
    };
  });

  // Sync filters → URL whenever filters change (skip first render)
  const skipFirstSync = useRef(true);
  useEffect(() => {
    if (skipFirstSync.current) { skipFirstSync.current = false; return; }
    syncFiltersToUrl(filters, setSearchParams);
  }, [filters, setSearchParams]);

  // Initialize scope filter on first load (only if URL didn't already set territories)
  useEffect(() => {
    if (!initializedRef.current && shouldScope && salesRepName) {
      // Only auto-scope if URL didn't already provide territories
      if (!filters.territories || filters.territories.length === 0) {
        const repTerritories = REP_TERRITORY_MAP[salesRepName];
        if (repTerritories) {
          setFilters(prev => ({ ...prev, territories: repTerritories, offset: 0 }));
        }
        setScopeActive(true);
      } else {
        // URL had territories — check if they match rep territories to set scope badge
        const repTerritories = REP_TERRITORY_MAP[salesRepName];
        setScopeActive(JSON.stringify(filters.territories?.sort()) === JSON.stringify(repTerritories?.sort()));
      }
      initializedRef.current = true;
    } else if (!initializedRef.current) {
      initializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot init effect; filters.territories must not retrigger
  }, [shouldScope, salesRepName]);

  const toggleScope = () => {
    if (scopeActive) {
      // Show all — remove territory filter
      setFilters(prev => ({ ...prev, territories: undefined, offset: 0 }));
      setScopeActive(false);
    } else if (salesRepName) {
      // Re-apply scope
      const repTerritories = REP_TERRITORY_MAP[salesRepName];
      if (repTerritories) {
        setFilters(prev => ({ ...prev, territories: repTerritories, offset: 0 }));
      }
      setScopeActive(true);
    }
  };

  const { data: filterOptions } = useAccountFilterOptions();
  const { data: result, isLoading, error, refetch } = useAccounts(filters);
  const exportMutation = useExportAccounts();

  const accounts = result?.data || [];
  const totalCount = result?.totalCount ?? 0;
  const pageSize = filters.limit || 25;

  const handleExport = async () => {
    try {
      const data = await exportMutation.mutateAsync(filters);
      const exportData = data.map((account) => ({
        'Company Name': account.company_name || '',
        'Domain': account.domain || '',
        'Category': account.primary_category || '',
        'Region': account.region || '',
        'Country': account.headquarters_country || '',
        'State': account.headquarters_state || '',
        'Sales Rep': account.sales_rep || '',
        'Modality': account.carr_modality || '',
        'Pipeline Count': account.carr_pipeline_count || 0,
        'Highest Phase': account.carr_highest_phase || '',
        'Employee Range': account.employee_range || '',
        
      }));
      const csv = convertToCSV(exportData);
      const filename = `carr_accounts_export_${formatDateForFilename()}.csv`;
      downloadFile(csv, filename, 'text/csv;charset=utf-8;');
      toast.success(`Exported ${data.length} accounts`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export accounts');
    }
  };

  const pageIndex = Math.floor((filters.offset || 0) / pageSize);

  const handlePageSizeChange = (size: number) => {
    setFilters((prev) => ({ ...prev, limit: size, offset: 0 }));
  };

  const handlePageIndexChange = (index: number) => {
    setFilters((prev) => ({ ...prev, offset: index * (prev.limit || 25) }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="space-y-1 mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Accounts</h1>
          <p className="text-muted-foreground">Target account directory</p>
        </div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          <p className="font-semibold">Error loading accounts</p>
          <p className="text-sm mt-1">{error.message}</p>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => { setFilters({ limit: 25, offset: 0, hideInactive: true }); setTimeout(() => refetch(), 100); }}>
              Clear Filters & Retry
            </Button>
            <Button variant="outline" onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">Accounts</h1>
            {shouldScope && (
              <Button
                variant={scopeActive ? "secondary" : "outline"}
                size="sm"
                onClick={toggleScope}
                className="gap-1.5"
              >
                {scopeActive ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                {scopeActive ? `My Accounts` : 'Show All'}
              </Button>
            )}
            {scopeActive && salesRepName && (
              <Badge variant="secondary" className="text-xs">Filtered: {salesRepName}</Badge>
            )}
          </div>
          <p className="text-muted-foreground">Target account directory</p>
        </div>
        <div className="flex items-center gap-2">
          <CreateAccountDialog />
          <Button
            onClick={handleExport}
            disabled={exportMutation.isPending || totalCount === 0}
            variant="outline"
          >
            <Download className="mr-2 h-4 w-4" />
            {exportMutation.isPending ? 'Exporting...' : `Export CSV (${totalCount.toLocaleString()})`}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filter Panel */}
        <div className="lg:col-span-1">
          <AccountsFilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            filterOptions={filterOptions}
            totalCount={totalCount}
          />
        </div>

        {/* Table */}
        <div className="lg:col-span-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <p className="text-lg font-medium">No accounts found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
              <Button variant="outline" className="mt-4" onClick={() => setFilters({ limit: 25, offset: 0, hideInactive: true })}>
                Clear All Filters
              </Button>
            </div>
          ) : (
            <AccountsTable
              data={accounts}
              totalCount={totalCount}
              pageSize={pageSize}
              pageIndex={pageIndex}
              onPageSizeChange={handlePageSizeChange}
              onPageIndexChange={handlePageIndexChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Accounts;
